/**
 * Bank authorization via Plaid Link — the primary ACH rail (DEC-003).
 *
 * Link is Plaid's hosted flow: bank selection, credentials, and the NACHA debit authorization
 * all happen inside it, so no account number, routing number, or bank credential ever touches
 * this bundle (SPEC.md §16.5). When Link closes, this screen tells the server, and the server
 * asks Plaid what actually happened — the browser's word is never a payment state.
 */
import { useEffect, useRef, useState } from "react";
import { useDialogFocus } from "@givehere/ui";
import { completeBankLink } from "./api.js";

const PLAID_LINK_SRC = "https://cdn.plaid.com/link/v2/stable/link-initialize.js";

type PlaidLinkHandle = { open: () => void; destroy: () => void };
type PlaidLinkMetadata = {
  accounts?: ReadonlyArray<{ id?: string }>;
};

type PlaidGlobal = {
  create: (options: {
    token: string;
    onSuccess: (publicToken: string, metadata: PlaidLinkMetadata) => void;
    onExit: (error: unknown) => void;
  }) => PlaidLinkHandle;
};

/** Loads Plaid's script once per page. */
let plaidScript: Promise<PlaidGlobal | null> | null = null;
const loadPlaid = (): Promise<PlaidGlobal | null> => {
  plaidScript ??= new Promise((resolve) => {
    const existing = (window as { Plaid?: PlaidGlobal }).Plaid;
    if (existing) return resolve(existing);
    const script = document.createElement("script");
    script.src = PLAID_LINK_SRC;
    script.async = true;
    script.onload = () => resolve((window as { Plaid?: PlaidGlobal }).Plaid ?? null);
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
  return plaidScript;
};

export function PlaidLinkConfirm({
  linkToken,
  donationId,
  accessToken,
  amountLabel,
  organizationDisplayName,
  onAuthorized,
  onCancel,
}: {
  linkToken: string;
  donationId: string;
  accessToken: string;
  amountLabel: string;
  organizationDisplayName: string;
  /** The transfer is authorized and processing; the receipt screen takes it from here. */
  onAuthorized: () => void;
  onCancel: () => void;
}) {
  const panelRef = useDialogFocus<HTMLDivElement>();
  const [phase, setPhase] = useState<"loading" | "ready" | "linking" | "finishing">("loading");
  const [error, setError] = useState<string | null>(null);
  const handle = useRef<PlaidLinkHandle | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadPlaid().then((plaid) => {
      if (cancelled) return;
      if (!plaid) {
        setError("Could not open your bank.");
        return;
      }
      handle.current = plaid.create({
        token: linkToken,
        onSuccess: (publicToken, metadata) => {
          setPhase("finishing");
          // The public token rides to the server so a signed-in donor's bank is retained for
          // one-tap reuse (GH-331); the server exchanges it — this bundle never keeps it. The
          // account id is a hint the server validates against the item's own account list.
          completeBankLink(
            donationId,
            accessToken,
            publicToken ?? null,
            metadata?.accounts?.[0]?.id ?? null,
          )
            .then((result) => {
              if (result.state === "failed") {
                setError("Your bank did not authorize.");
                setPhase("ready");
                return;
              }
              // "processing" or "pending": either way the receipt screen polls for the truth.
              onAuthorized();
            })
            .catch(() => {
              setError("Could not confirm the authorization.");
              setPhase("ready");
            });
        },
        onExit: () => setPhase("ready"),
      });
      setPhase("ready");
    });
    return () => {
      cancelled = true;
      handle.current?.destroy();
      handle.current = null;
    };
    // The link token identifies one authorization session; a new token means a fresh mount.
  }, [linkToken, donationId, accessToken, onAuthorized]);

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="bank-title">
      <div className="sheet__panel" ref={panelRef} tabIndex={-1}>
        <button
          type="button"
          className="sheet__close"
          onClick={onCancel}
          aria-label="Go back"
          disabled={phase === "finishing"}
        >
          &#x2715;
        </button>

        <p className="sheet__amount" id="bank-title">
          {amountLabel}
        </p>
        <p className="sheet__to">in support of {organizationDisplayName}</p>

        {phase === "loading" && <p className="notice">Loading secure bank connection…</p>}
        {phase === "finishing" && <p className="notice">Confirming with your bank…</p>}

        {error && (
          <p className="notice notice--error" role="alert">
            {error}
          </p>
        )}

        {phase === "ready" && (
          <button
            type="button"
            className="pay"
            onClick={() => {
              setError(null);
              setPhase("linking");
              handle.current?.open();
            }}
          >
            Connect your bank
          </button>
        )}

        <p className="sheet__foot">
          Connected securely by Plaid. Bank transfers usually take a few days to settle; your
          receipt will show the gift as processing until it does.
        </p>
      </div>
    </div>
  );
}
